/**
 * IntentCards — Zone 1 of the intent-first generator (spec §3).
 * Five cards; tapping selects (re-tap deselects). Buttons are accelerators,
 * NEVER gates — typing below with nothing tapped is the first-class custom
 * path. Labels are the vocabulary-researched primaries: content-function
 * words only (never persona words, never "SEL" — see spec §2).
 *
 * 2026-07-29 UI pass: each intent carries a hue identity (same pattern as the
 * hue-coded sidebar icons) — an icon tile + tinted selected state replaces
 * the anonymous gray rows. Hover states via the .icCard class (inline styles
 * can't express :hover).
 */

import { useTranslation } from 'react-i18next';
import { Icon, IconTile } from '../../../ui/index.jsx';

export const INTENTS = [
  { key: 'teach',     labelKey: 'intentCards.teach.label',     subtitleKey: 'intentCards.teach.subtitle',     emoji: '📚', hue: '52,211,153'  }, // emerald
  { key: 'motivate',  labelKey: 'intentCards.motivate.label',  subtitleKey: 'intentCards.motivate.subtitle',  emoji: '🌟', hue: '251,191,36'  }, // amber
  { key: 'classroom', labelKey: 'intentCards.classroom.label', subtitleKey: 'intentCards.classroom.subtitle', emoji: '📋', hue: '56,189,248'  }, // sky
  { key: 'school',    labelKey: 'intentCards.school.label',    subtitleKey: 'intentCards.school.subtitle',    emoji: '🎉', hue: '167,139,250' }, // violet
  { key: 'custom',    labelKey: 'intentCards.custom.label',    subtitleKey: 'intentCards.custom.subtitle',    emoji: '✨', hue: '244,114,182' }, // pink
];

// Brightivity (handoff 02-AI-Poster-Maker.png): kit icon tiles instead of emoji, one colour per intent.
const KIT = { teach: ['printables', 'blue'], motivate: ['sparkle', 'yellow'], classroom: ['template', 'green'], school: ['school', 'coral'], custom: ['wand', 'purple'] };

export default function IntentCards({ value, onChange, hideHeader = false }) {
  const { t } = useTranslation('home');
  return (
    <div>
      {!hideHeader && (
        <div className="bv-intents__head">
          <h2>{t('intentCards.heading')}</h2>
          <span>{t('intentCards.pickOne')}</span>
        </div>
      )}
      <div className="bv-intents" role="group" aria-label={t('intentCards.heading')}>
        {INTENTS.map((intent) => {
          const active = value === intent.key;
          const [icon, color] = KIT[intent.key] || ['sparkle', 'blue'];
          return (
            <button key={intent.key} type="button" className={`bv-intent${active ? ' is-on' : ''}`} onClick={() => onChange(active ? null : intent.key)} aria-pressed={active}>
              <IconTile icon={icon} color={color} size={56} iconSize={26} />
              <span className="bv-intent__text">
                <span className="bv-intent__title">{t(intent.labelKey)}</span>
                {/* Subtitle is load-bearing (vocab research) — always rendered. */}
                <span className="bv-intent__sub">{t(intent.subtitleKey)}</span>
              </span>
              {active && <span className="bv-intent__check" aria-hidden="true"><Icon name="check" size={16} /></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
